import {
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/documents-table-os/definitions";

/**
 * Upload a file to S3 using a presigned URL
 */
export const uploadFileToS3 = async (
  file: File,
  uploadUrlResponse: UploadUrlResponse,
): Promise<void> => {
  const result = await fetch(uploadUrlResponse.url, {
    method: "PUT",
    body: file,
    headers: {
      "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
    },
  });

  if (result.status !== 200) {
    throw new Error(`S3 upload failed with status ${result.status}`);
  }
};

/**
 * Get upload URL request for a file
 */
export const createUploadUrlRequest = (
  prefix: string,
  fileName: string,
  teamName: string,
  securityLevel = "Normal",
  uniqueKey?: string,
): UploadUrlRequest & { uniqueKey?: string } => {
  const request: UploadUrlRequest & { uniqueKey?: string } = {
    prefix,
    fileName,
    teamName,
    securityLevel,
  };

  if (uniqueKey) {
    request.uniqueKey = uniqueKey;
  }

  return request;
};

/**
 * Build S3 key for team files
 */
export const buildS3Key = (
  prefix: string,
  uniqueKey: string,
  fileName: string,
): string => {
  return `${prefix}/${uniqueKey}/${fileName}`;
};

/**
 * Extract uniqueKey from a kb-native documentId (`{team}/kb/{uniqueKey}/{fileName}`).
 * Returns null for any other shape.
 */
export const extractUniqueKey = (documentId: string): string | null => {
  if (!documentId) return null;
  const match = documentId.match(
    /(?:^|\/)kb\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(?:\/|$)/,
  );
  return match ? match[1] : null;
};

/**
 * Validate file name for forbidden characters
 */
export const validateFileName = (fileName: string): boolean => {
  const invalidCharactersRegex = /["*/:<>?|]/;
  return !invalidCharactersRegex.test(fileName);
};

/**
 * Get document URL from presigned URL (remove query params)
 */
export const getDocumentUrlFromPresigned = (presignedUrl: string): string => {
  return decodeURIComponent(presignedUrl.split("?")[0]);
};

/**
 * Batch items into groups of specified size
 */
export function batchItems<T>(items: T[], batchSize: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }
  return batches;
}

/**
 * Sleep for specified milliseconds (useful for rate limiting)
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
